<script setup lang="ts">
import { onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import {
  DBtn,
  EyeIcon,
  DModal,
  DLoading,
  DotsGridIcon,
  PlusIcon,
  HelpCircleIcon,
  CircleIcon,
  CheckCircleBrokenIcon,
  Edit03Icon,
  XIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  Lock03Icon,
  LockUnlocked03Icon
} from 'front-library'
import DynamicForm from './DynamicForm.vue'
import Popper from 'vue3-popper'
import { Field, getForm } from '@/app/core/services/forms'
import { snakeToCamelObject } from '@/app/core/util/strings'
import { validateTranslate } from '@/app/core/composable/general'
import { useI18n } from 'vue-i18n'
import useDraggableList from './useDraggableList'

const { columns, rows, loadData, loadRow, setField, replaceIdsByName } =
  useDraggableList()

const props = defineProps<{
  field: Field
  subForm?: string
  readonly?: boolean
  label?: any
  modelValue?: any
  allModel?: any
  error?: any
  actions?: any
  disabled?: any
  options?: any
  class?: any
}>()

const emit = defineEmits(['update:modelValue'])
const route = useRoute()
const { t } = useI18n()

const customForm = ref<any>({})
const updatedRows = ref<any[]>([])
const rowModel = ref<any>({})
const isOpenModal = ref(false)
const modalTitle = ref('')
const isReadOnly = ref(false)
const currentId = ref('')
const titleBtnSave = ref('')
const customRows = ref<any[]>([])
const labelTranslate = ref(validateTranslate(props.label ?? ''))

const loading = ref(false)
const showInactives = ref(false)

watch(
  () => updatedRows.value.length,
  () => {
    void updateViewRows()
  }
)
watch(
  () => rows.value,
  newRows => {
    if (updatedRows.value.length === 0 && newRows.length > 0) {
      updatedRows.value = [...newRows]
    }
    void updateViewRows()
  }
)

const updateViewRows = async () => {
  const cloneUpdatedRows = JSON.parse(JSON.stringify(updatedRows.value))
  const updatedMap = new Map()
  cloneUpdatedRows.forEach((row: any) => {
    updatedMap.set(row.id || row.actionId, row)
  })

  let updatedRowsUnreplaced: any[]

  if (cloneUpdatedRows.length > 0) {
    updatedRowsUnreplaced = [...cloneUpdatedRows]

    for (const originalRow of rows.value) {
      if (
        !cloneUpdatedRows.some(
          (updatedRow: any) => updatedRow.id === originalRow.id
        )
      ) {
        updatedRowsUnreplaced.push(originalRow)
      }
    }
  } else {
    updatedRowsUnreplaced = rows.value.map(
      (originalRow: any) => updatedMap.get(originalRow.id) || originalRow
    )
  }
  const updatedRowReplaced: any[] = []
  for (const row of updatedRowsUnreplaced) {
    updatedRowReplaced.push(await replaceIdsByName(row))
  }

  const filteredRows = showInactives.value
    ? updatedRowReplaced
    : updatedRowReplaced.filter(
        row =>
          row.isActive !== false ||
          ['edited', 'deactivated'].includes(row.action)
      )

  customRows.value = filteredRows
  loading.value = false

  emitUpdate()
}

const actionsConf = new Map<string, any>([
  [
    'added',
    {
      class: 'text-green-600 bg-green-100',
      dot: 'bg-green-600',
      text: t('form.button.addRegistry'),
      tooltip: t('form.button.cancelRegistration')
    }
  ],
  [
    'edited',
    {
      class: 'text-blue-600 bg-blue-100',
      dot: 'bg-blue-600',
      text: t('form.button.editRegistry'),
      tooltip: t('form.button.cancelEdit')
    }
  ],
  [
    'activated',
    {
      class: 'text-emerald-600 bg-emerald-100',
      dot: 'bg-green-600',
      text: t('notification.activated')
    }
  ],
  [
    'deactivated',
    {
      class: 'text-orange-600 bg-orange-100',
      dot: 'bg-orange-600',
      text: t('notification.disabled')
    }
  ]
])

const add = async () => {
  loading.value = true
  titleBtnSave.value = t('form.button.save')
  rowModel.value = {
    action: 'added'
  }
  modalTitle.value = `${t('form.button.add')} ${labelTranslate.value}`
  customForm.value = await getForm({ entity: props.subForm ?? '' })
  isOpenModal.value = true
  emit('update:modelValue', updatedRows)
  loading.value = false
}

const saveAdd = async (values: any) => {
  values.action = values.id ? 'edited' : 'added'
  values.actionId = values.id || Date.now()
  if (values.id) {
    const existingIndex = updatedRows.value.findIndex(
      row => row.id === values.id || row.actionId === values.actionId
    )

    if (existingIndex !== -1) {
      updatedRows.value[existingIndex] = values
      updateViewRows()
    } else {
      updatedRows.value.unshift(values)
    }
  } else {
    updatedRows.value.unshift(values)
  }

  isOpenModal.value = false
  loading.value = true
}

const update = async (id: any, currentId?: string) => {
  loading.value = true
  titleBtnSave.value = t('form.button.update')
  rowModel.value = await loadRow(id, currentId)
  modalTitle.value = `${t('table.action.edit')} ${labelTranslate.value}`
  customForm.value = await getForm({ entity: props.subForm ?? '' })
  isOpenModal.value = true
  loading.value = false
}

const closeModal = () => {
  isReadOnly.value = false
  isOpenModal.value = false
}

const deleteFields = (obj: any) => {
  const columnNames = new Set(columns.value.map((column: any) => column.name))
  columnNames.add('action')
  columnNames.add('actionId')
  columnNames.add('id')
  for (const key in obj) {
    if (!columnNames.has(key)) {
      delete obj[key]
    }
  }
  return obj
}

const cancelAction = (actionId: string) => {
  loading.value = true
  updatedRows.value = updatedRows.value.filter(
    (row: any) => row.actionId !== actionId
  )
}

const toggleActiveState = (values: any) => {
  loading.value = true
  const newIsActive = !values.isActive

  const originalRow = rows.value.find(row => row.id === values.id)
  const isRevertingToOriginal = newIsActive === (originalRow?.isActive ?? true)
  const action = isRevertingToOriginal
    ? null
    : newIsActive
    ? 'activated'
    : 'deactivated'

  const findRowIndex = (array: any[]) =>
    array.findIndex(row => row.id === values.id)

  const customRowIndex = findRowIndex(customRows.value)
  if (customRowIndex !== -1) {
    customRows.value[customRowIndex] = {
      ...customRows.value[customRowIndex],
      isActive: newIsActive,
      ...(action && { action })
    }
  }

  const updatedRowIndex = findRowIndex(updatedRows.value)

  const newValues = deleteFields({
    ...originalRow,
    action,
    actionId: values.actionId || Date.now(),
    isActive: newIsActive
  })

  if (updatedRowIndex !== -1) {
    updatedRows.value[updatedRowIndex] = {
      ...updatedRows.value[updatedRowIndex],
      action,
      actionId: values.actionId || Date.now(),
      isActive: newIsActive
    }
  } else {
    updatedRows.value.push(newValues)
  }
}

const emitUpdate = () => {
  const newObj = JSON.parse(JSON.stringify(updatedRows.value))
  emit(
    'update:modelValue',
    newObj.map((row: any) => {
      delete row.actionId
      delete row.createdAt
      delete row.updatedAt
      for (let key in row) {
        if (
          typeof row[key] === 'string' &&
          (row[key].match(/^[\d,]+\.\d{2}$/) || row[key].match(/%/))
        ) {
          let cleaned = row[key].replace(/,/g, '')
          cleaned = cleaned.replace(/\.00$/, '')
          cleaned = cleaned.replace(/%$/, '')
          row[key] = cleaned
        }
      }
      return snakeToCamelObject(row)
    })
  )
}

const draggedIndex = ref<number>(-1)
const dropZoneIndex = ref<number>(-1)

const onDragStart = (event: DragEvent, index: number) => {
  draggedIndex.value = index
  if (event.dataTransfer) {
    event.dataTransfer.effectAllowed = 'move'
    event.dataTransfer.setData('text/plain', index.toString())

    const dragRow = (event.target as HTMLElement)?.closest('tr')
    if (dragRow) {
      const clonedRow = dragRow.cloneNode(true) as HTMLElement
      clonedRow.style.position = 'absolute'
      clonedRow.style.top = '-1000px'
      clonedRow.style.left = '-1000px'
      clonedRow.style.width = dragRow.offsetWidth + 'px'
      clonedRow.style.backgroundColor = '#f3f4f6'
      clonedRow.style.opacity = '0.8'
      clonedRow.style.border = '2px solid #3b82f6'
      clonedRow.style.borderRadius = '8px'
      document.body.appendChild(clonedRow)

      event.dataTransfer.setDragImage(clonedRow, event.offsetX, event.offsetY)

      setTimeout(() => {
        document.body.removeChild(clonedRow)
      }, 100)
    }
  }
}

const onDragOver = (event: DragEvent, index: number) => {
  event.preventDefault()
  if (event.dataTransfer) {
    event.dataTransfer.dropEffect = 'move'
  }
  dropZoneIndex.value = index
}

const onDragLeave = () => {
  dropZoneIndex.value = -1
}

const onDrop = (event: DragEvent, dropIndex: number) => {
  event.preventDefault()

  if (draggedIndex.value === dropIndex || draggedIndex.value === -1) {
    resetDragState()
    return
  }

  const newRows = [...customRows.value]
  const [draggedRow] = newRows.splice(draggedIndex.value, 1)
  newRows.splice(dropIndex, 0, draggedRow)
  customRows.value = newRows

  const allElementsMap = new Map()

  updatedRows.value.forEach(row => {
    allElementsMap.set(row.id, row)
  })

  rows.value.forEach(row => {
    if (!allElementsMap.has(row.id)) {
      allElementsMap.set(row.id, row)
    }
  })

  const allElementsArray = Array.from(allElementsMap.values())
  const newOrderedElements: any[] = []

  newRows.forEach(visibleRow => {
    const fullElement = allElementsMap.get(visibleRow.id)
    if (fullElement) {
      newOrderedElements.push(fullElement)
    }
  })

  allElementsArray.forEach(element => {
    if (!newRows.some(visibleRow => visibleRow.id === element.id)) {
      newOrderedElements.push(element)
    }
  })

  const newUpdatedRows: any[] = []

  newOrderedElements.forEach(element => {
    const processedElement = {
      ...element
    }

    newUpdatedRows.push(processedElement)
  })

  updatedRows.value = newUpdatedRows

  setTimeout(() => {
    emitUpdate()
  }, 0)

  resetDragState()
}

const resetDragState = () => {
  draggedIndex.value = -1
  dropZoneIndex.value = -1
}

const toggleShowInactives = () => {
  showInactives.value = !showInactives.value
  void updateViewRows()
}

const swapRows = (index: number, targetIndex: number) => {
  if (targetIndex < 0 || targetIndex >= customRows.value.length) return
  ;[customRows.value[index], customRows.value[targetIndex]] = [
    customRows.value[targetIndex],
    customRows.value[index]
  ]

  setTimeout(() => {
    emitUpdate()
  }, 0)
}

const moveRowUp = (index: number) => {
  swapRows(index, index - 1)
}

const moveRowDown = (index: number) => {
  swapRows(index, index + 1)
}

onMounted(async () => {
  emitUpdate()
  setField(props.field)
  currentId.value = route.params.id as string
  await loadData(currentId.value)
})
</script>
<template>
  <div class="sm:col-span-6 lg:col-span-6 xl:col-span-6" :class="class">
    <DLoading v-show="loading" :message="t('loading.data')" />
    <div>
      <DModal
        :open="isOpenModal"
        :title="modalTitle"
        size="md:w-full lg:w-full xl:w-4/6"
        :form="true"
        @keydown.esc="closeModal">
        <template #form>
          <div class="text-lg text-gray-800 font-semibold">
            {{ modalTitle }}
          </div>
          <DynamicForm
            ref="form"
            v-if="isOpenModal"
            :form-schema="customForm"
            :initial-model="rowModel"
            :readonly="isReadOnly"
            :height-form-modal="true"
            @cancel="closeModal"
            :title-btn-save="titleBtnSave"
            :has-disable-audit="true"
            @submit="saveAdd" />
        </template>
        <template #actions>
          <div></div>
        </template>
      </DModal>
      <div class="bg-white pb-4 w-full">
        <div class="sm:flex justify-end">
          <div class="flex space-x-2">
            <DBtn color="secondary" type="button" @click="toggleShowInactives">
              {{
                showInactives
                  ? t('form.button.hideInactives')
                  : t('form.button.showInactives')
              }}
              <EyeIcon class="h-6 w-6 ml-2 cursor-pointer text-gray-600" />
            </DBtn>

            <DBtn
              v-if="field?.props?.actions.includes('add')"
              type="button"
              @click="add()">
              <PlusIcon class="text-white-800" />
            </DBtn>
          </div>
        </div>
      </div>
    </div>
    <div class="overflow-x-auto bg-white shadow-md rounded-lg">
      <table class="min-w-full divide-y divide-gray-200">
        <tbody class="bg-white divide-y divide-gray-200">
          <tr
            v-for="(row, index) in customRows"
            :key="row.id || index"
            class="hover:bg-gray-50 transition-colors duration-200"
            :class="{
              'bg-blue-50': draggedIndex === index,
              'border-t-2 border-blue-400': dropZoneIndex === index,
              'opacity-50': draggedIndex === index,
              'bg-gray-50 opacity-80': row.isActive === false,
              'text-gray-400': row.isActive === false
            }"
            @dragover="onDragOver($event, index)"
            @dragleave="onDragLeave"
            @drop="onDrop($event, index)">
            <td class="px-3 py-4 whitespace-nowrap align-middle">
              <div class="flex items-center justify-center h-10">
                <div
                  class="drag-handle cursor-move flex items-center justify-center text-gray-400 hover:text-gray-600"
                  :class="{ 'opacity-50': row.isActive === false }"
                  :draggable="true"
                  @dragstart="onDragStart($event, index)"
                  @dragend="resetDragState">
                  <DotsGridIcon
                    class="h-6 w-6 cursor-grab hover:cursor-grabbing text-gray-600" />
                </div>
              </div>
            </td>
            <td
              class="px-6 py-4 whitespace-nowrap text-md font-medium align-middle"
              :class="'text-gray-900'">
              <div class="flex items-center gap-2">
                <div
                  v-if="row.isActive === false"
                  class="w-2 h-2 bg-gray-400 rounded-full"></div>
                <span>{{ row.name || '-' }}</span>
              </div>
            </td>
            <td class="px-6 py-4 whitespace-nowrap text-md align-middle">
              <span
                class="inline-flex items-center px-2.5 py-0.5 rounded-full font-medium"
                :class="
                  row.isActive === false
                    ? 'bg-gray-100 text-gray-600'
                    : 'bg-blue-100 text-blue-700'
                ">
                {{ row.type || '-' }}
              </span>
            </td>
            <td class="px-6 py-4 text-md align-middle">
              <div class="flex flex-row items-center justify-center gap-2 h-10">
                <CheckCircleBrokenIcon
                  v-if="row.isMandatory"
                  class="h-5 w-5"
                  :class="
                    row.isActive === false ? 'text-gray-400' : 'text-blue-600'
                  " />
                <CircleIcon
                  v-else
                  class="h-5 w-5"
                  :class="
                    row.isActive === false ? 'text-gray-400' : 'text-blue-600'
                  " />
                <span class="mr-1" :class="'text-gray-900'">
                  {{
                    row.isMandatory
                      ? t('fields.required')
                      : t('fields.optional')
                  }}
                </span>
              </div>
            </td>
            <td
              class="px-6 py-4 whitespace-nowrap text-md align-middle"
              :class="'text-gray-900'">
              {{ t('fields.minimun', [row.minLength || '-']) }}
            </td>
            <td
              class="px-6 py-4 whitespace-nowrap text-md align-middle"
              :class="'text-gray-900'">
              {{ t('fields.maximun', [row.maxLength || '-']) }}
            </td>
            <td
              class="px-6 py-4 whitespace-nowrap text-md align-middle"
              :class="
                row.isActive === false ? 'text-gray-400' : 'text-gray-900'
              ">
              <div class="flex items-center justify-center h-10">
                <Popper
                  arrow
                  offsetDistance="12"
                  :content="
                    row.placeholder ||
                    validateTranslate('form.placeholder', [row.name])
                  "
                  :hover="true"
                  placement="top"
                  class="tooltip">
                  <HelpCircleIcon
                    class="h-5 w-5 text-gray-400 hover:text-gray-600 cursor-pointer" />
                </Popper>
              </div>
            </td>
            <td
              v-if="!readonly"
              class="px-6 py-4 whitespace-nowrap text-right text-md font-medium align-middle">
              <div class="flex justify-end gap-1 items-center h-10">
                <Popper
                  v-if="row.action"
                  arrow
                  offsetDistance="12"
                  :content="actionsConf.get(row.action)?.tooltip"
                  :hover="true"
                  placement="top"
                  class="tooltip">
                  <div
                    class="flex items-center rounded-full font-semibold w-fit text-sm py-1 px-3"
                    :class="[
                      actionsConf.get(row.action)?.class,
                      ['activated', 'deactivated'].includes(row.action)
                        ? 'cursor-not-allowed'
                        : 'cursor-pointer'
                    ]"
                    @click="
                      ;['activated', 'deactivated'].includes(row.action) ||
                        cancelAction(row.actionId)
                    ">
                    <div
                      class="mr-2 w-1.5 h-1.5 rounded-full"
                      :class="actionsConf.get(row.action)?.dot"></div>
                    {{ actionsConf.get(row.action)?.text }}
                    <XIcon
                      v-if="
                        !['activated', 'deactivated'].includes(row.action)
                      " />
                  </div>
                </Popper>

                <Popper
                  v-if="field?.props?.actions.includes('edit')"
                  arrow
                  offsetDistance="12"
                  :content="t('table.action.edit')"
                  :hover="true"
                  placement="top"
                  class="tooltip">
                  <div
                    class="bg-gray-50 rounded-md py-2"
                    :class="
                      ['activated', 'deactivated'].includes(row.action) ||
                      row.isActive === false
                        ? 'opacity-50 cursor-not-allowed'
                        : 'cursor-pointer'
                    "
                    @click="
                      ;['activated', 'deactivated'].includes(row.action) ||
                        row.isActive === false ||
                        update(row.id, currentId)
                    ">
                    <Edit03Icon class="h-6 w-6 mx-2 text-gray-600" />
                  </div>
                </Popper>

                <Popper
                  arrow
                  offsetDistance="12"
                  :hover="true"
                  placement="top"
                  class="tooltip">
                  <div
                    class="bg-gray-50 rounded-md py-2"
                    :class="
                      customRows.indexOf(row) === 0
                        ? 'opacity-50 cursor-not-allowed'
                        : 'cursor-pointer'
                    "
                    @click="
                      customRows.indexOf(row) === 0 ||
                        moveRowUp(customRows.indexOf(row))
                    ">
                    <ChevronUpIcon class="h-6 w-6 mx-2 text-gray-600" />
                  </div>
                </Popper>
                <Popper
                  arrow
                  offsetDistance="12"
                  :hover="true"
                  placement="top"
                  class="tooltip">
                  <div
                    class="bg-gray-50 rounded-md py-2"
                    :class="
                      customRows.indexOf(row) === customRows.length - 1
                        ? 'opacity-50 cursor-not-allowed'
                        : 'cursor-pointer'
                    "
                    @click="
                      customRows.indexOf(row) === customRows.length - 1 ||
                        moveRowDown(customRows.indexOf(row))
                    ">
                    <ChevronDownIcon class="h-6 w-6 mx-2 text-gray-600" />
                  </div>
                </Popper>

                <Popper
                  v-if="field?.props?.actions.includes('enable')"
                  arrow
                  offsetDistance="12"
                  :content="
                    row.isActive
                      ? t('table.action.disable')
                      : t('table.action.enable')
                  "
                  :hover="true"
                  placement="top"
                  class="tooltip">
                  <div
                    class="bg-gray-50 rounded-md py-2"
                    :class="
                      ['added', 'edited'].includes(row.action)
                        ? 'opacity-50 cursor-not-allowed'
                        : 'cursor-pointer'
                    "
                    @click="
                      ;['added', 'edited'].includes(row.action) ||
                        (toggleActiveState(row), updateViewRows())
                    ">
                    <Lock03Icon
                      v-if="row.isActive"
                      class="h-6 w-6 mx-2 text-gray-600" />
                    <LockUnlocked03Icon
                      v-else
                      class="h-6 w-6 mx-2 text-gray-600" />
                  </div>
                </Popper>
              </div>
            </td>
          </tr>

          <tr v-if="customRows.length === 0">
            <td
              :colspan="7"
              class="px-6 py-4 whitespace-nowrap text-md text-gray-500 text-center">
              {{ t('table.noData') }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<style scoped>
.drag-handle {
  cursor: grab;
  user-select: none;
}

.drag-handle:active {
  cursor: grabbing;
}

.drag-handle[draggable='true']:hover {
  color: #4f46e5;
}

.drag-handle * {
  pointer-events: none;
}
</style>
